import { ItineraryActivity } from "./activity.model";
import { DayView, ItineraryDay } from "./itinerary-day.model";
import {
  DayAxis,
  axisDayCount,
  compareDayKeys,
  datesAxis,
  dayIndexFromKey,
  dayKeyFromIndex,
  isTemplateDayKey,
} from "./day-axis";

/**
 * Rappresenta un singolo giorno nell'itinerario con metadata
 */
export class ItineraryDayBuilder implements DayView {
  /** Identificativo del giorno: data ISO oppure `D<n>` sui template (vedi day-axis.ts). */
  key: string;
  /** Data reale del giorno, null sui template. */
  date: string | null;
  /** Posizione 1-based del giorno nell'itinerario. */
  index: number;
  activities: ItineraryActivity[] = [];

  // Flags per analisi del giorno
  hasCheckIn: boolean = false;
  hasCheckOut: boolean = false;
  isWithinAccommodation: boolean = false; // Giorno coperto da alloggio ma senza check-in/out
  isFreeDay: boolean = false; // Solo trasporti/alloggi, nessuna attività
  hasFlights: boolean = false;
  hasTransportation: boolean = false;
  hasActivities: boolean = false; // Attività turistiche effettive
  accommodationId: string | null = null; // ID dell'alloggio che copre questo giorno
  /** Il giorno cade fuori dalla finestra della travel request collegata. */
  exceedsTravelRequest: boolean = false;

  constructor(key: string, activities: ItineraryActivity[] = [], index: number = 1) {
    this.key = key;
    this.date = isTemplateDayKey(key) ? null : key;
    this.index = index;
    this.activities = activities;
    this.updateMetadata();
  }

  /**
   * Aggiorna i metadata del giorno analizzando le attività
   */
  updateMetadata(): void {
    this.hasCheckIn = false;
    this.hasCheckOut = false;
    this.hasFlights = false;
    this.hasTransportation = false;
    this.hasActivities = false;
    this.accommodationId = null;

    for (const activity of this.activities) {
      // Check accommodation
      if (activity.type === 'accommodation') {
        if (activity.inOrOutActivity === 'in') {
          this.hasCheckIn = true;
          this.accommodationId = activity.id;
        } else if (activity.inOrOutActivity === 'out') {
          this.hasCheckOut = true;
        }
      }

      // Check transportation types
      if (activity.type === 'flight') {
        this.hasFlights = true;
      } else if (activity.type === 'transportation') {
        this.hasTransportation = true;
      }

      // Check actual activities (things to do)
      if (activity.type === 'activity' || activity.type === 'events' || activity.type === 'food-drinks') {
        this.hasActivities = true;
      }
    }

    // Determina se è un giorno libero
    this.isFreeDay = !this.hasActivities && 
                      !this.hasCheckIn && 
                      !this.hasCheckOut && 
                      this.activities.length > 0;
  }

  /**
   * Aggiunge un'attività al giorno
   */
  addActivity(activity: ItineraryActivity): void {
    activity.location = String(this.activities.length);
    this.activities.push(activity);
    this.updateMetadata();
  }

  /**
   * Rimuove un'attività dal giorno tramite ID
   */
  removeActivity(activityId: string): boolean {
    const initialLength = this.activities.length;
    this.activities = this.activities.filter(act => act.id !== activityId);
    const removed = this.activities.length < initialLength;
    
    if (removed) {
      this.updateMetadata();
    }
    
    return removed;
  }

  /**
   * Trova un'attività tramite ID
   */
  findActivity(activityId: string): ItineraryActivity | undefined {
    return this.activities.find(act => act.id === activityId);
  }

  /**
   * Sostituisce un'attività esistente
   */
  updateActivity(activityId: string, newActivity: ItineraryActivity): boolean {
    const index = this.activities.findIndex(act => act.id === activityId);
    if (index !== -1) {
      this.activities[index] = newActivity;
      this.updateMetadata();
      return true;
    }
    return false;
  }

  /**
   * Restituisce un summary del giorno
   */
  getSummary(): string {
    if (this.activities.length === 0) return 'Empty day';
    
    const parts: string[] = [];
    if (this.hasCheckIn) parts.push('Check-in');
    if (this.hasCheckOut) parts.push('Check-out');
    if (this.hasFlights) parts.push('Flight');
    if (this.hasTransportation) parts.push('Transport');
    if (this.hasActivities) parts.push(`${this.activities.filter(a => a.type === 'activity' || a.type === 'events').length} activities`);
    
    return parts.join(' • ') || 'Day with activities';
  }
}

/**
 * Classe principale per gestire l'intero itinerario
 */
export class ItineraryBuilder {
  private days: Map<string, ItineraryDayBuilder> = new Map();
  /** Asse dei giorni: date reali o giorni relativi (template). */
  axis: DayAxis | null = null;
  startDate: string | null = null;
  endDate: string | null = null;
  /** Finestra della travel request collegata, per marcare i giorni in eccesso. */
  private requestWindow: { start: string; end: string } | null = null;

  constructor(axis?: DayAxis) {
    if (axis) {
      this.initializeDays(axis);
    }
  }

  /**
   * Inizializza tutti i giorni dell'asse.
   */
  initializeDays(axis: DayAxis | null): void {
    if (!axis) return;
    this.axis = axis;
    this.startDate = axis.mode === 'dates' ? axis.startDate : null;
    this.endDate = axis.mode === 'dates' ? axis.endDate : null;
    this.days.clear();

    const count = axisDayCount(axis);
    for (let i = 1; i <= count; i++) {
      const key = dayKeyFromIndex(axis, i);
      this.days.set(key, new ItineraryDayBuilder(key, [], i));
    }
  }

  /**
   * Dichiara la finestra della travel request collegata: i giorni che ne escono
   * vengono marcati (il builder non li nasconde, li segnala).
   */
  setTravelRequestWindow(start?: string | null, end?: string | null): void {
    this.requestWindow = start && end ? { start, end } : null;
    this.updateExceedingDays();
  }

  private updateExceedingDays(): void {
    for (const day of this.days.values()) {
      day.exceedsTravelRequest = !!(
        this.requestWindow &&
        day.date &&
        (day.date < this.requestWindow.start || day.date > this.requestWindow.end)
      );
    }
  }

  /**
   * Carica giorni da un array esistente
   * Se startDate/endDate sono forniti, genera tutti i giorni nell'intervallo
   * altrimenti usa solo i giorni presenti nei dati
   */
  loadFromArray(
    daysData: { key?: string; date?: string | null; activities: ItineraryActivity[] | Partial<ItineraryActivity>[] }[],
    axis?: DayAxis | null,
  ): void {
    const keyOf = (dayData: { key?: string; date?: string | null }) => dayData.key || dayData.date || '';

    if (axis) {
      // initializeDays azzera e ricrea i giorni dell'asse.
      this.initializeDays(axis);

      // Poi popola i giorni con le attività esistenti
      for (const dayData of daysData) {
        const key = keyOf(dayData);
        if (!key) continue;

        const day = this.days.get(key);
        if (day) {
          // Rimuovi attività esistenti e aggiungi quelle nuove
          day.activities = dayData.activities as ItineraryActivity[];
          day.updateMetadata();
        } else if (dayData.activities.length > 0) {
          // Il giorno esce dall'intervallo ma ha attività (es. check-out il giorno dopo
          // l'ultimo, o un template più lungo della richiesta): va conservato e mostrato,
          // non scartato in silenzio.
          const extraDay = new ItineraryDayBuilder(
            key,
            dayData.activities as ItineraryActivity[],
            dayIndexFromKey(axis, key),
          );
          this.days.set(key, extraDay);
        }
      }
    } else {
      console.warn('⚠️ No axis provided, loading only days with activities');
      this.days.clear();
      // Fallback: importa solo i giorni con attività
      for (const dayData of daysData) {
        const key = keyOf(dayData);
        if (!key) continue;
        this.days.set(key, new ItineraryDayBuilder(key, dayData.activities as ItineraryActivity[]));
      }

      // Ricostruisci l'asse dai giorni caricati
      const keys = Array.from(this.days.keys()).sort(compareDayKeys);
      if (keys.length > 0 && !isTemplateDayKey(keys[0])) {
        this.startDate = keys[0];
        this.endDate = keys[keys.length - 1];
        this.axis = datesAxis(this.startDate, this.endDate);
      }
      this.reindexDays();
    }

    // Analizza accommodation coverage
    this.updateAccommodationCoverage();
    this.updateExceedingDays();
  }

  /** Riallinea la posizione 1-based dei giorni dopo un caricamento fuori asse. */
  private reindexDays(): void {
    this.getAllDays().forEach((day, i) => {
      day.index = this.axis ? dayIndexFromKey(this.axis, day.key) : i + 1;
    });
  }

  /**
   * Aggiunge un'attività a un giorno specifico
   */
  addActivity(dayKey: string, activity: ItineraryActivity): void {
    this.addActivities([[dayKey, activity]]);
  }

  /**
   * Inserisce più attività in un colpo solo.
   *
   * Serve agli alloggi, che entrano come coppia check-in/check-out: con due
   * `addActivity` separate la copertura verrebbe ricalcolata due volte, la prima
   * su uno stato in cui il check-out non esiste ancora.
   */
  addActivities(entries: [string, ItineraryActivity][]): void {
    let daysAdded = false;
    let touchesAccommodation = false;

    for (const [dayKey, activity] of entries) {
      let day = this.days.get(dayKey);

      // Se il giorno non esiste, crealo
      if (!day) {
        day = new ItineraryDayBuilder(
          dayKey,
          [],
          this.axis ? dayIndexFromKey(this.axis, dayKey) : this.days.size + 1,
        );
        this.days.set(dayKey, day);
        daysAdded = true;
      }

      day.addActivity(activity);
      touchesAccommodation ||= activity.type === 'accommodation';
    }

    if (daysAdded) this.updateExceedingDays();
    if (touchesAccommodation) this.updateAccommodationCoverage();
  }

  /**
   * Rimuove un'attività da tutti i giorni (utile per accommodation con in/out)
   */
  removeActivityById(activityId: string): string[] {
    const affectedDates: string[] = [];
    
    // Rimuovi anche l'attività out associata se è un accommodation
    const idsToRemove = [activityId, `${activityId}-out`];
    
    for (const [date, day] of this.days.entries()) {
      for (const id of idsToRemove) {
        if (day.removeActivity(id)) {
          affectedDates.push(date);
        }
      }
    }
    
    // Aggiorna la copertura accommodation
    this.updateAccommodationCoverage();
    
    return affectedDates;
  }

  /**
   * Aggiorna i flag isWithinAccommodation per tutti i giorni
   */
  private updateAccommodationCoverage(): void {
    // Reset tutti i flag
    for (const day of this.days.values()) {
      day.isWithinAccommodation = false;
    }

    // Trova tutti gli accommodation, come intervallo di posizioni 1-based:
    // sui template l'alloggio è ancorato agli indici, non alle date.
    const accommodations: { id: string; startIndex: number; endIndex: number }[] = [];

    for (const day of this.days.values()) {
      for (const activity of day.activities) {
        if (activity.type === 'accommodation' && activity.inOrOutActivity === 'in') {
          const startIndex = this.indexOfAnchor(
            activity.start_date || activity.date,
            activity.start_day_index ?? activity.day_index,
            day.index,
          );
          const endIndex = this.indexOfAnchor(
            activity.end_date || activity.date,
            activity.end_day_index ?? activity.day_index,
            startIndex,
          );
          accommodations.push({ id: activity.id, startIndex, endIndex });
        }
      }
    }

    // Marca i giorni coperti da accommodation
    for (const acc of accommodations) {
      for (let i = acc.startIndex; i <= acc.endIndex; i++) {
        const day = this.axis ? this.days.get(dayKeyFromIndex(this.axis, i)) : undefined;

        if (day) {
          day.isWithinAccommodation = true;
          day.accommodationId = acc.id;
        }
      }
    }
  }

  /** Posizione 1-based di un ancoraggio (data reale o indice), con fallback. */
  private indexOfAnchor(date: string | null | undefined, dayIndex: number | null | undefined, fallback: number): number {
    if (this.axis?.mode === 'index') return dayIndex ?? fallback;
    if (this.axis && date) return dayIndexFromKey(this.axis, date);
    return fallback;
  }

  /**
   * Ottiene un giorno specifico
   */
  getDay(dayKey: string): ItineraryDayBuilder | undefined {
    return this.days.get(dayKey);
  }

  /**
   * Trova un'attività ovunque e restituisce l'attività e il giorno a cui apparteneva
   */
  findActivityById(activityId: string): { activity: ItineraryActivity; key: string } | null {
    for (const [key, day] of this.days.entries()) {
      const found = day.findActivity(activityId);
      if (found) return { activity: found, key };
    }
    return null;
  }

  /**
   * Sposta un'attività (rimuovendola dalla sua posizione) e la inserisce prima di un'altra attività nel giorno target
   * Se targetActivityId è null, append alla fine del giorno target
   */
  moveActivityBefore(sourceActivityId: string, targetDate: string, targetActivityId?: string): boolean {
    // Trova source
    const found = this.findActivityById(sourceActivityId);
    if (!found) return false;

    const { activity } = found;

    // Rimuovi dal giorno sorgente
    const sourceDate = found.key;
    this.removeActivityById(sourceActivityId);

    // Assicurati che il giorno target esista
    let targetDay = this.days.get(targetDate);
    if (!targetDay) {
      targetDay = new ItineraryDayBuilder(
        targetDate,
        [],
        this.axis ? dayIndexFromKey(this.axis, targetDate) : this.days.size + 1,
      );
      this.days.set(targetDate, targetDay);
    }

    if (!targetActivityId) {
      // append
      targetDay.addActivity(activity);
      // reindex activities for the target day (and source day)
      this.reindexDay(targetDate);
      if (sourceDate && sourceDate !== targetDate) this.reindexDay(sourceDate);
      // aggiorna coverage accommodation dopo lo spostamento
      this.updateAccommodationCoverage();
      return true;
    }

    // trova indice target
    const idx = targetDay.activities.findIndex(a => a.id === targetActivityId);
    if (idx === -1) {
      // se non trovato, append
      targetDay.addActivity(activity);
      return true;
    }

    // inserisci prima dell'indice
    targetDay.activities.splice(idx, 0, activity);
    targetDay.updateMetadata();

    // Reindicizza le attività del giorno target e del giorno sorgente (se diverso)
    this.reindexDay(targetDate);
    if (sourceDate && sourceDate !== targetDate) this.reindexDay(sourceDate);

    // aggiorna coverage accommodation dopo lo spostamento
    this.updateAccommodationCoverage();

    return true;
  }

  /**
   * Imposta il campo `location` (stringa) per tutte le attività del giorno
   * in base alla loro posizione nell'array (0..n)
   */
  private reindexDay(date: string): void {
    const day = this.days.get(date);
    if (!day) return;
    for (let i = 0; i < day.activities.length; i++) {
      try {
        // salvo come stringa per mantenere compatibilità con DB/text fields
        (day.activities[i] as any).location = String(i);
      } catch (e) {
        // ignore
      }
    }
    day.updateMetadata();
  }

  /**
   * Restituisce tutti i giorni ordinati per data
   */
  getAllDays(): ItineraryDayBuilder[] {
    return Array.from(this.days.values()).sort((a, b) =>
      compareDayKeys(a.key, b.key)
    );
  }

  /**
   * Restituisce i giorni come array semplice per il salvataggio
   */
  toArray(): ItineraryDay[] {
    return this.getAllDays().map(day => new ItineraryDay({
      key: day.key,
      date: day.date,
      day_index: day.index,
      activities: day.activities as ItineraryActivity[]
    }));
  }

  /**
   * Analisi dell'itinerario: giorni senza alloggio
   */
  getDaysWithoutAccommodation(): string[] {
    const days: string[] = [];
    const lastIndex = this.axis ? axisDayCount(this.axis) : this.days.size;

    for (const day of this.days.values()) {
      // Escludi primo e ultimo giorno (potrebbero essere solo trasferimenti)
      if (day.index === 1 || day.index === lastIndex) continue;

      if (!day.isWithinAccommodation && !day.hasCheckIn) {
        days.push(day.key);
      }
    }

    return days;
  }

  /**
   * Analisi: giorni completamente vuoti
   */
  getEmptyDays(): string[] {
    return this.getAllDays()
      .filter(day => day.activities.length === 0)
      .map(day => day.key);
  }

  /**
   * Analisi: giorni con solo trasporti (possibili giorni di transito)
   */
  getTransitDays(): string[] {
    return this.getAllDays()
      .filter(day => day.isFreeDay)
      .map(day => day.key);
  }

  /**
   * Conta attività per tipo, escludendo le 'out'
   */
  getActivityStats(): Record<string, number> {
    const stats: Record<string, number> = {};
    for (const day of this.days.values()) {
      for (const activity of day.activities) {
        if (activity.inOrOutActivity === 'out') continue;
        stats[activity.type] = (stats[activity.type] || 0) + 1;
      }
    }
    return stats;
  }

  /**
   * Verifica se l'itinerario è valido (ha almeno un giorno con attività)
   */
  isValid(): boolean {
    return this.getAllDays().some(day => day.activities.length > 0);
  }

  /**
   * Ottiene un report completo dell'itinerario
   */
  getReport(): {
    totalDays: number;
    daysWithActivities: number;
    emptyDays: number;
    daysWithoutAccommodation: string[];
    transitDays: string[];
    activityStats: Record<string, number>;
    isValid: boolean;
  } {
    return {
      totalDays: this.days.size,
      daysWithActivities: this.getAllDays().filter(d => d.activities.length > 0).length,
      emptyDays: this.getEmptyDays().length,
      daysWithoutAccommodation: this.getDaysWithoutAccommodation(),
      transitDays: this.getTransitDays(),
      activityStats: this.getActivityStats(),
      isValid: this.isValid()
    };
  }
}
